import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import {
  AppLayout,
  Box,
  Table,
  Header,
  SpaceBetween,
  Spinner,
  Button,
  Modal
} from '@amzn/awsui-components-react';
import SideNav from './SideNav';
import { listAllCruzerSchedulesAdmin, deleteCruzerJobSchedule } from '../Cruzer/Utils/CruzerAPIs';
import { getStatusIndicator } from '../Cruzer/Utils/GeneralUtils';
import { fetchApps } from '../Templates/Utils/AppActivityUtils';
import { checkIfPortalAdmin } from 'src/checkPageAccess';
import AccessDenied from '../../AccessDenied';
import SchedulerSetupModal from '../Cruzer/SchedulerSetupModal';
import RefreshHistoryModal from '../Cruzer/RefreshHistoryModal';

const AllCruzerSchedules = () => {
  const [schedules, setSchedules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [appNameMap, setAppNameMap] = useState({});
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [accessChecked, setAccessChecked] = useState(false);
  const [NavOpen, setNavOpen] = useState(true);

  const [selectedSchedule, setSelectedSchedule] = useState(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showDeleteSuccess, setShowDeleteSuccess] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [modalMode, setModalMode] = useState('view');

  const userId = useSelector((globalState) => globalState.auth.userId);
  const currentStage = useSelector((globalState) => globalState.auth.currentStage);

  useEffect(() => {
    checkAccess();
  }, [userId, currentStage]);

  const checkAccess = async () => {
    if (userId && currentStage) {
      const isAdmin = await checkIfPortalAdmin(userId, currentStage);
      setHasAdminAccess(isAdmin);
      setAccessChecked(true);
      if (isAdmin) {
        loadData();
        loadAppNames();
      }
    }
  };

  const loadAppNames = () => {
    fetchApps().then(apps => {
      const map = {};
      apps.forEach(app => { map[app.app_id] = app.app_name; });
      setAppNameMap(map);
    }).catch(() => {});
  };

  const loadData = () => {
    setLoading(true);
    listAllCruzerSchedulesAdmin(
      (response) => {
        const data = response.data?.listAllCruzerSchedulesAdmin || [];
        const formatted = data.map(schedule => ({
          schedulerId: schedule.schedule_id,
          schedulerName: schedule.schedule_name,
          schedule_type: schedule.schedule_type,
          cron_expression: schedule.cron_expression,
          description: schedule.description,
          dailyRefreshCount: schedule.daily_schedule_count ?? 'N/A',
          status: schedule.is_active ? 'Active' : 'Disabled',
          reference_id: schedule.reference_id,
          target_type: schedule.target_type,
          target_identifier: schedule.target_identifier,
          app_ids: schedule.app_ids || [],
          created_by: schedule.created_by,
          created_at: schedule.created_at,
          last_updated_by: schedule.last_updated_by,
          last_updated_at: schedule.last_updated_at,
          begin_schedule_on: schedule.begin_schedule_on,
          originalData: schedule
        }));
        setSchedules(formatted);
        setLoading(false);
      },
      (error) => {
        console.error('Failed to load all schedules:', error);
        setSchedules([]);
        setLoading(false);
      }
    );
  };

  const handleView = (item) => {
    setModalMode('view');
    setSelectedSchedule(item);
    setShowViewModal(true);
  };

  const handleEdit = (item) => {
    setModalMode('edit');
    setSelectedSchedule(item);
    setShowEditModal(true);
  };

  const handleDeleteConfirm = (item) => {
    setSelectedSchedule(item);
    setShowDeleteConfirm(true);
  };

  const handleDelete = () => {
    if (!selectedSchedule?.schedulerId) return;
    setDeleteLoading(true);
    deleteCruzerJobSchedule(
      { schedule_id: selectedSchedule.schedulerId },
      () => {
        setDeleteLoading(false);
        setShowDeleteConfirm(false);
        setShowDeleteSuccess(true);
        loadData();
      },
      (error) => {
        console.error('Failed to delete schedule:', error);
        setDeleteLoading(false);
        setShowDeleteConfirm(false);
      }
    );
  };

  const columnDefinitions = [
    {
      id: 'schedule_name',
      header: 'Schedule Name',
      cell: item => item.schedulerName,
      sortingField: 'schedulerName',
      width: 200
    },
    {
      id: 'schedule_type',
      header: 'Type',
      cell: item => item.schedule_type,
      sortingField: 'schedule_type',
      width: 120
    },
    {
      id: 'daily_schedule_count',
      header: 'Daily Refresh Count',
      cell: item => item.dailyRefreshCount,
      sortingField: 'dailyRefreshCount',
      width: 160
    },
    {
      id: 'status',
      header: 'Status',
      cell: item => getStatusIndicator(item.status),
      sortingField: 'status',
      width: 120
    },
    {
      id: 'reference_id',
      header: 'Reference ID',
      cell: item => item.reference_id,
      width: 180
    },
    {
      id: 'target_type',
      header: 'Target Type',
      cell: item => item.target_identifier?.includes('qs-refresh') ? 'QS Refresh' : item.target_type,
      width: 130
    },
    {
      id: 'associated_apps',
      header: 'Associated Apps',
      cell: item => (
        <Box>
          {item.app_ids.map((appId, index) => (
            <div key={index}>{appNameMap[appId] || appId}</div>
          ))}
        </Box>
      ),
      width: 200
    },
    {
      id: 'created_by',
      header: 'Created By',
      cell: item => item.created_by,
      sortingField: 'created_by',
      width: 130
    },
    {
      id: 'actions',
      header: 'Actions',
      cell: item => (
        <SpaceBetween direction="horizontal" size="xs">
          <Button variant="icon" iconName="search" ariaLabel="View" onClick={() => handleView(item)} />
          <Button variant="icon" iconName="edit" ariaLabel="Edit" onClick={() => handleEdit(item)} />
          <Button variant="icon" iconName="history" ariaLabel="Refresh history" onClick={() => { setSelectedSchedule(item); setShowHistoryModal(true); }} />
          <Button variant="icon" iconName="remove" ariaLabel="Delete" onClick={() => handleDeleteConfirm(item)} />
        </SpaceBetween>
      ),
      width: 160
    }
  ];

  const Content = (
    <>
      {!accessChecked ? (
        <Box textAlign="center" padding={{ top: 'xxxl' }}>
          <Spinner size="large" /> Checking access...
        </Box>
      ) : !hasAdminAccess ? (
        <AccessDenied appName="Admin panel" />
      ) : (
        <Box padding={{ vertical: 'l', horizontal: 'l' }}>
          <Table
            columnDefinitions={columnDefinitions}
            items={schedules}
            loading={loading}
            loadingText="Loading all Cruzer schedules..."
            empty={
              <Box textAlign="center" color="inherit">
                <b>No schedules found</b>
                <Box padding={{ bottom: 's' }} variant="p" color="inherit">
                  No Cruzer schedules exist across any app.
                </Box>
              </Box>
            }
            header={
              <Header
                counter={`(${schedules.length})`}
                actions={
                  <Button iconName="refresh" onClick={loadData}>
                    Refresh
                  </Button>
                }
              >
                Cruzer Admin Panel
              </Header>
            }
          />

          <RefreshHistoryModal
            visible={showHistoryModal}
            onDismiss={() => { setShowHistoryModal(false); setSelectedSchedule(null); }}
            schedule={selectedSchedule}
          />

          <SchedulerSetupModal
            key={`${modalMode}-${selectedSchedule?.schedulerId || 'none'}`}
            visible={showViewModal || showEditModal}
            onDismiss={() => {
              setShowViewModal(false);
              setShowEditModal(false);
              setSelectedSchedule(null);
            }}
            initialData={selectedSchedule}
            mode={modalMode}
            loadData={loadData}
            allDatasets={schedules}
            setDatasets={setSchedules}
            setShowEditModal={setShowEditModal}
            setShowAddModal={() => {}}
            setSelectedDataset={setSelectedSchedule}
            setShowAddSuccess={() => {}}
            setAddedScheduleName={() => {}}
            dataFromMeta={[]}
            datasetOptions={[]}
          />

          <Modal
            visible={showDeleteConfirm}
            onDismiss={() => setShowDeleteConfirm(false)}
            header="Confirm Delete"
            footer={
              <Box float="right">
                <SpaceBetween direction="horizontal" size="xs">
                  <Button variant="link" onClick={() => setShowDeleteConfirm(false)}>
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleDelete}
                    loading={deleteLoading}
                    disabled={deleteLoading}
                  >
                    Delete
                  </Button>
                </SpaceBetween>
              </Box>
            }
          >
            <Box>
              Are you sure you want to delete the schedule{' '}
              <strong>{selectedSchedule?.schedulerName}</strong>?
            </Box>
          </Modal>

          <Modal
            visible={showDeleteSuccess}
            onDismiss={() => setShowDeleteSuccess(false)}
            header="Delete Successful"
            footer={
              <Box float="right">
                <Button variant="primary" onClick={() => setShowDeleteSuccess(false)}>
                  OK
                </Button>
              </Box>
            }
          >
            <Box>
              Schedule <strong>{selectedSchedule?.schedulerName}</strong> has been successfully deleted.
            </Box>
          </Modal>
        </Box>
      )}
    </>
  );

  return (
    <AppLayout
      disableContentPaddings={true}
      content={Content}
      navigation={<SideNav activeHref="/AllCruzerSchedules" />}
      contentType="default"
      navigationOpen={NavOpen}
      onNavigationChange={() => setNavOpen(!NavOpen)}
      toolsHide={true}
      headerVariant="high-contrast"
    />
  );
};

export default AllCruzerSchedules;
